/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import clsx from 'clsx';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import FormItem from '@ion/dls/src/components/forms/FormItem';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {PegFieldProps} from '../../types';

const useStyles = makeStyles(() => ({
  checkboxFormItem : {
    display                       : 'flex',
    alignItems                    : 'center',
    width                         : '100%',
    maxWidth                      : 'none',
    '& .MuiFormControlLabel-root' : {
      margin : 0
    },
    '& .MuiCheckbox-root' : {
      backgroundColor : dlsColor('white'),
      borderRadius    : 'unset',
      padding         : '5px'
    }
  },
  label : {
    marginLeft : '8px',
    color      : dlsColor('grey1'),
    cursor     : 'pointer'
  },
  labelDisabled : {
    color  : dlsColor('grey3'),
    cursor : 'default'
  }
}));

const toCheckboxId = (label: string): string => (
  `peg-checkbox-field-${label.replace(/\s+/g, '-').toLowerCase()}`
);

/**
 * Defines the `PegCheckboxField` component, which renders a checkbox
 * for displaying and managing boolean parameters. The tooltip wraps only
 * the checkbox control - not the label - so hovering the label text does
 * not open it; clicking the label still toggles the checkbox via the
 * native `label`/`htmlFor` association.
 *
 * ```typescript
 *
 *    <PegCheckboxField
 *      name={name}
 *      label={label}
 *      checked={value}
 *      onChange={function(){}} />
 *
 * ```
 */
const PegCheckboxField = ({label, value, tooltip, onChange, disabled}: PegFieldProps) => {
  const classes    = useStyles();
  const checkboxId = toCheckboxId(label);

  return (
    <FormItem className={classes.checkboxFormItem}>
      <Tooltip title={tooltip}>
        <span>
          <CheckBox id={checkboxId}
            name={label}
            checked={Boolean(value)}
            disabled={disabled}
            onChange={function({target: {checked}}) {
              onChange(checked);
            }} />
        </span>
      </Tooltip>
      <label htmlFor={checkboxId} className={clsx(classes.label, {[classes.labelDisabled]: disabled})}>
        {label}
      </label>
    </FormItem>
  );
};

export {PegCheckboxField};
